import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../global/functions/apiClient";
import { getAuthHeaders } from "../global/functions/authorization";
const apiUrl = process.env.REACT_APP_API_URL;
const initialState = {
    items: [],
    status: "idle",
    error: null,
    success: null,
    loading: false,
};

export const exportRestaurantData = createAsyncThunk(
    "database/exportRestaurantData",
    async (restaurantId) => {
        try {
            const response = await axios.get(
                `${apiUrl}/database/export`,
                getAuthHeaders(false, restaurantId)
            );
            return response?.data;
        } catch (err) {
            throw new Error(err.response?.data?.message || err.message);
        }
    }
);

export const importRestaurantData = createAsyncThunk(
    "database/importRestaurantData",
    async ({ body, restaurantId }) => {
        try {
            const response = await axios.post(
                `${apiUrl}/database/import`,
                body,
                getAuthHeaders(true, restaurantId)
            );
            return response?.data;
        } catch (err) {
            throw new Error(err.response?.data?.message || err.message);
        }
    }
);

export const downloadExportedFile = createAsyncThunk(
    "backup/downloadExportedFile",
    async ({ filename, restaurantId }, { rejectWithValue }) => {
        try {
            const config = {
                responseType: "blob", // Essential for downloading files
                ...getAuthHeaders(false, restaurantId), // Add authentication headers
            };
            const { data } = await axios.get(
                `${apiUrl}/database/download?file=${filename}`, // Use correct API URL
                config
            );
            // Return the blob data and filename for the component to handle
            return { data, filename };
        } catch (error) {
            // Handle errors, including blob responses
            let message = error.message;
            if (error.response) {
                if (error.response.data instanceof Blob) {
                    // If the error is a blob, try to read it as text
                    const text = await error.response.data.text();
                    try {
                        const parsed = JSON.parse(text);
                        message = parsed.message || message;
                    } catch {
                        message = text || message;
                    }
                } else {
                    message = error.response.data.message || message;
                }
            }
            return rejectWithValue(message);
        }
    }
);



const databasesSlice = createSlice({
    name: "databases",
    initialState,
    reducers: {
        updateStatus: (state) => {
            state.status = "idle";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(exportRestaurantData.pending, (state, action) => {
                state.status = "loadingFetchData";
                state.loading = true;
            })
            .addCase(exportRestaurantData.fulfilled, (state, action) => {
                state.status = "fetchData";
                state.loading = false;
                state.items = action.payload;
            })
            .addCase(exportRestaurantData.rejected, (state, action) => {
                state.status = "fetchError";
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(importRestaurantData.pending, (state, action) => {
                state.status = "loading";
                state.loading = true;
            })
            .addCase(importRestaurantData.fulfilled, (state, action) => {
                state.status = "addSuccess";
                state.loading = false;
                state.success = action.payload.message;
            })
            .addCase(importRestaurantData.rejected, (state, action) => {
                state.status = "addError";
                state.loading = false;
                state.error = action.error.message;
            });
    },
});

export const { updateStatus } = databasesSlice.actions;
export const selectAllDatabases = (state) => state.databases.items;
export const getDatabasesStatus = (state) => state.databases.status;
export const getDatabasesError = (state) => state.databases.error;
export const getDatabasesSuccess = (state) => state.databases.success;
export const getDatabasesLoading = (state) => state.databases.loading;
export default databasesSlice.reducer;
